import type { MechanicDefinition, MechanicEvent, EventReaction, MechanicContext } from "./types";

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

export const gameDirector: MechanicDefinition = {
  id: "game_director",
  name: "Game Director",
  description: "Controls the Game Master's personality, rules enforcement, difficulty scaling, and session pacing.",
  icon: "Crown",
  category: "narrative",
  alwaysActive: true,
  aiHints: {
    purpose: "Unified framework for GM logic: archetype, pacing, difficulty scaling, rules enforcement, and direct out-of-band chat.",
    whenToUse: "Always active. Core to AI logic and game orchestration.",
    synergies: [],
    conflicts: [],
    prerequisites: [],
    tags: ["gm", "director", "difficulty", "pacing", "rules", "chat"],
    complexity: 3,
    exampleConfig: { archetype: "storyteller", baseline: "adaptive" },
    stateVariables: [
      "_story_arc_phase", "_dramatic_tension", "_turns_since_plot_advance",
      "_consecutive_failures", "_consecutive_successes", "_dc_modifier",
      "_pacing_phase", "_turns_in_phase", "_turns_since_encounter", "_encounter_count"
    ],
  },
  configSchema: [
    {
      key: "archetype",
      label: "GM Archetype",
      type: "select",
      options: [
        { value: "classic", label: "Classic DM (fair, balanced)" },
        { value: "storyteller", label: "Storyteller (narrative-first)" },
        { value: "adversarial", label: "Adversarial (challenging, cunning)" },
        { value: "improvisational", label: "Improvisational (chaotic, yes-and)" },
        { value: "methodical", label: "Methodical (structured, tactical)" },
        { value: "comedic", label: "Comedic (funny, absurd situations)" },
      ],
      default: "storyteller",
    },
    {
      key: "player_agency",
      label: "Player Agency",
      type: "select",
      options: [
        { value: "railroad", label: "Railroad (firm story direction)" },
        { value: "guided", label: "Guided (nudges toward plot)" },
        { value: "sandbox", label: "Sandbox (fully player-driven)" },
      ],
      default: "guided",
    },
    {
      key: "consequence_severity",
      label: "Consequence Severity",
      type: "select",
      options: [
        { value: "forgiving", label: "Forgiving (soft landings)" },
        { value: "fair", label: "Fair (logical, proportional)" },
        { value: "harsh", label: "Harsh (lasting impact)" },
        { value: "brutal", label: "Brutal (unforgiving)" },
      ],
      default: "fair",
    },
    {
      key: "strictness",
      label: "Rules Strictness",
      type: "select",
      options: [
        { value: "loose", label: "Loose (rules are suggestions)" },
        { value: "flexible", label: "Flexible (bend when fun)" },
        { value: "strict", label: "Strict (rules as written)" },
      ],
      default: "flexible",
    },
    {
      key: "baseline_difficulty",
      label: "Difficulty Setting",
      type: "select",
      options: [
        { value: "easy", label: "Easy" },
        { value: "normal", label: "Normal (fair challenge)" },
        { value: "hard", label: "Hard" },
        { value: "adaptive", label: "Adaptive (adjusts to player skill)" },
      ],
      default: "adaptive",
    },
    {
      key: "pacing_rhythm",
      label: "Pacing Rhythm",
      type: "select",
      options: [
        { value: "action_heavy", label: "Action Heavy" },
        { value: "balanced", label: "Balanced" },
        { value: "slow_burn", label: "Slow Burn" },
        { value: "episodic", label: "Episodic" },
      ],
      default: "balanced",
    },
    {
      key: "allow_gm_chat",
      label: "Enable Out-of-Band GM Chat",
      type: "boolean",
      default: true,
      description: "Allows the player to use a floating chat bubble to talk to the GM about rules, hints, or mechanics.",
    },
    {
      key: "allow_hints",
      label: "Allow GM Hints",
      type: "boolean",
      default: true,
      description: "GM can give gameplay hints to the player in chat."
    },
    {
      key: "allow_knowledge_sharing",
      label: "Allow Lore/Knowledge Questions",
      type: "boolean",
      default: true,
    },
    {
      key: "allow_rules_explanation",
      label: "Allow Rules Explanation",
      type: "boolean",
      default: true,
    },
    {
      key: "allow_mechanic_changes",
      label: "Allow Mechanic Changes",
      type: "boolean",
      default: false,
    }
  ],

  initializeState: () => ({
    _story_arc_phase: "setup",
    _dramatic_tension: 3,
    _turns_since_plot_advance: 0,
    _gm_plot_threads: [],
    _gm_narration_notes: [],
    _gm_nudge_needed: false,
    _gm_nudge_suggestion: "",
    _consecutive_failures: 0,
    _consecutive_successes: 0,
    _dc_modifier: 0,
    _pacing_phase: "exploration",
    _turns_in_phase: 0,
    _turns_since_encounter: 0,
  }),

  systemPromptSection: (config) => {
    const archetype = config.archetype || "storyteller";
    const agency = config.player_agency || "guided";
    const severity = config.consequence_severity || "fair";
    const strictness = config.strictness || "flexible";
    const baseline = config.baseline_difficulty || "adaptive";
    const rhythm = config.pacing_rhythm || "balanced";
    
    // GM Chat rules
    const gmChat = config.allow_gm_chat !== false;
    const hints = config.allow_hints !== false;
    const rules = config.allow_rules_explanation !== false;
    const mechChanges = config.allow_mechanic_changes === true;

    return `## GAME DIRECTOR
- You are playing the role of the Game Master. Set your GM Archetype to: ${archetype}.
- Player Agency: ${agency}. Consequence Severity: ${severity}. Rules Strictness: ${strictness}.
- Difficulty: ${baseline === "adaptive" ? "Dynamically adjust difficulty based on player successes/failures." : `Maintain a ${baseline} difficulty baseline.`}
- Pacing: ${rhythm}. Use this rhythm to cycle between exploration, tension, action, and rest. Avoid letting the story stagnate.

**Features Available to Director:**
- Plot Threads: Manage active narrative beats via \`flag_plot_thread\` tools.
- Narrator Notes: Drop multi-turn planning notes using \`gm_narration_note\`.
- Arc Management: Advance the story arc logically via \`advance_story_arc\`.
${gmChat ? `- GM Chat: The player has access to an out-of-band GM chat.
  - ${hints ? "ALLOWED" : "DISALLOWED"}: Give gameplay hints if asked.
  - ${rules ? "ALLOWED" : "DISALLOWED"}: Explain rules and math explicitly.
  - ${mechChanges ? "ALLOWED" : "DISALLOWED"}: Toggle game mechanics at player request.` : ""}
`;
  },

  postTurnHook: (context: MechanicContext): EventReaction | null => {
    const vars = context.state.variables || {};
    const config = context.config || {};
    const notes: string[] = [];
    const updates: Record<string, any> = {};

    // 1. Difficulty & Rubber Banding Check
    let failures = vars._consecutive_failures || 0;
    let successes = vars._consecutive_successes || 0;
    if (vars._last_check_success !== undefined) {
      if (vars._last_check_success) { successes++; failures = 0; }
      else { failures++; successes = 0; }
      updates._last_check_success = undefined;
      updates._consecutive_failures = failures;
      updates._consecutive_successes = successes;

      if (config.baseline_difficulty === "adaptive") {
        let dcMod = vars._dc_modifier || 0;
        if (failures >= 3) { dcMod = Math.max(-3, dcMod - 1); notes.push(`🎯 Director: ${failures} failures — DC eased to ${dcMod}, offer indirect help.`); }
        else if (successes >= 4) { dcMod = Math.min(3, dcMod + 1); notes.push(`🎯 Director: ${successes} successes — DC increased +${dcMod}, escalate threat.`); }
        updates._dc_modifier = dcMod;
      }
    }

    // 2. Plot Stagnation Check
    const currentTurn = vars._current_turn || 0;
    const turnsSincePlot = (vars._turns_since_plot_advance || 0) + 1;
    updates._turns_since_plot_advance = turnsSincePlot;
    if (turnsSincePlot > 5) {
      updates._gm_nudge_needed = true;
      notes.push(`🔔 STAGNATION: ${turnsSincePlot} turns without major plot advancement. Introduce a consequence, NPC, or complication to drive the story forward.`);
    }

    // 3. Pacing Phases
    const phase = vars._pacing_phase || "exploration";
    const turnsInPhase = (vars._turns_in_phase || 0) + 1;
    const turnsSinceEncounter = (vars._turns_since_encounter || 0) + 1;
    updates._turns_in_phase = turnsInPhase;
    updates._turns_since_encounter = turnsSinceEncounter;

    const thresholds: Record<string, Record<string, number>> = {
      action_heavy: { exploration: 2, tension: 1, action: 3, climax: 1, rest: 1 },
      balanced: { exploration: 3, tension: 2, action: 3, climax: 1, rest: 2 },
      slow_burn: { exploration: 5, tension: 3, action: 2, climax: 2, rest: 2 },
      episodic: { exploration: 3, tension: 2, action: 2, climax: 1, rest: 1 },
    };
    const rhythmStr = (config.pacing_rhythm as string) || "balanced";
    const limit = thresholds[rhythmStr]?.[phase] || 3;
    if (turnsInPhase >= limit) {
      // climax → rest is explicit: if the AI-set climax phase times out, fall back to rest not exploration.
      const transitions: Record<string, string> = { exploration: "tension", tension: "action", action: "rest", climax: "rest", rest: "exploration" };
      const nextPhase = transitions[phase] || "exploration";
      updates._pacing_phase = nextPhase;
      updates._turns_in_phase = 0;
      notes.push(`🎭 Pacing: Shift from ${phase} to ${nextPhase} phase. Adjust tone accordingly.`);
    }
    
    // 4. Clean Expired Notes
    const activeNotes = (vars._gm_narration_notes || []).filter((n: any) => n.expires_turn > currentTurn);
    if (activeNotes.length !== (vars._gm_narration_notes || []).length) {
      updates._gm_narration_notes = activeNotes;
    }
    for (const note of activeNotes) { notes.push(`📝 GM Note: ${note.text}`); }

    return { stateUpdates: updates, narrativeNotes: notes.length > 0 ? notes : undefined };
  },

  onEvent: (event) => {
    if (event.type === "skill_check_complete") {
      return { stateUpdates: { _last_check_success: event.data.success } };
    }
    if (event.type === "combat_end" || event.type === "combat_kill") {
      return { stateUpdates: { _turns_since_encounter: 0, _last_check_success: true, _turns_since_plot_advance: 0 } };
    }
    if (event.type === "rest_completed") {
      return { stateUpdates: { _pacing_phase: "exploration", _turns_in_phase: 0 } };
    }
    if (event.type === "quest_complete" || event.type === "quest_advanced") {
      return { stateUpdates: { _turns_since_plot_advance: 0 } };
    }
    return null;
  },

  tools: [
    {
      type: "function",
      function: {
        name: "advance_story_arc",
        description: "Manually advance the story arc phase or tension curve.",
        parameters: {
          type: "object",
          properties: {
            new_phase: { type: "string", enum: ["setup", "rising", "climax", "falling", "resolution"] },
            tension_adjustment: { type: "number", description: "Change tension level (+/-)" },
            reason: { type: "string" },
          },
          required: ["new_phase", "reason"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "flag_plot_thread",
        description: "Register or update a plot thread.",
        parameters: {
          type: "object",
          properties: {
            thread_id: { type: "string" },
            title: { type: "string" },
            status: { type: "string", enum: ["active", "dormant", "resolved", "failed"] },
          },
          required: ["thread_id", "title", "status"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "gm_narration_note",
        description: "Leave an internal GM note for future turns.",
        parameters: {
          type: "object",
          properties: {
            note: { type: "string" },
            duration_turns: { type: "number", description: "How many turns" },
          },
          required: ["note"],
        },
      },
    }
  ],
};
